import {
  useMutation,
  type Query,
  type QueryClient,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import customerKeys from '@/api/queries/customers/keyFactory';
import projectKeys from '@/api/queries/project/keyFactory';
import { graphql } from '@/gql';
import {
  type CoordinatesInput,
  type UpdateAddressCoordinatesMutation,
} from '@/gql/graphql';

const UpdateAddressCoordinates = graphql(`
  mutation UpdateAddressCoordinates($input: UpdateAddressInput!) {
    updateAddress(input: $input) {
      id
      coordinates {
        latitude
        longitude
      }
      lookupCoordinates {
        latitude
        longitude
      }
      manualCoordinates {
        latitude
        longitude
      }
      coordinatesSource
    }
  }
`);

export type UpdateAddressCoordinatesVariables = {
  id: string;
  coordinates: CoordinatesInput | null;
};

const isProjectOverviewQuery = (query: Query) =>
  query.queryKey[0] === projectKeys.all[0] &&
  query.queryKey[1] !== projectKeys.search('')[1] &&
  query.queryKey[2] === 'overview';

const isCustomerCoordinateQuery = (query: Query) => {
  const [scope, customerId, section] = query.queryKey;
  if (scope !== customerKeys.all[0]) return false;

  if (section === 'addresses') return true;

  return (
    query.queryKey.length === 2 &&
    customerId !== customerKeys.customers()[1] &&
    customerId !== customerKeys.existingCustomerContactEmails[1]
  );
};

export const invalidateAddressCoordinateQueries = (
  queryClient: QueryClient,
  projectId: string
) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: projectKeys.overview(projectId),
    }),
    // Addresses are shared, so refresh other cached project overviews too.
    queryClient.invalidateQueries({
      predicate: (query) =>
        isProjectOverviewQuery(query) && query.queryKey[1] !== projectId,
    }),
    queryClient.invalidateQueries({
      predicate: isCustomerCoordinateQuery,
    }),
  ]);

const useUpdateAddressCoordinates = (
  projectId: string,
  options?: UseMutationOptions<
    UpdateAddressCoordinatesMutation,
    unknown,
    UpdateAddressCoordinatesVariables
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input) =>
      gqlClient.request(UpdateAddressCoordinates, { input }),
    ...options,
    onSuccess: (...args) => {
      void invalidateAddressCoordinateQueries(queryClient, projectId);
      options?.onSuccess?.(...args);
    },
  });
};

export default useUpdateAddressCoordinates;
